import React, { useContext, useMemo, useRef } from "react";
import { Box } from "@material-ui/core";
import BackButton from "../../shared/components/BackButton";
import {
  CAPTURE_MODES,
  DOCUMENT_ID_VERIFICATION_STEP_IDS,
  DOCUMENT_SIDES,
  ID_DOCUMENT_VERIFICATION_EXCEPTIONS,
  ID_DOCUMENT_VERIFICATION_STATUSES,
  idDocumentVerificationExceptionMatches,
} from "./config";
import { useDocumentIdVerification } from "./hooks/useDocumentIdVerification";
import { DocumentTypeSelection } from "./steps/DocumentTypeSelection";
import { ProcessInformation } from "./steps/ProcessInformation";
import { DocumentIdPicture } from "./steps/DocumentIdPicture/DocumentIdPicture";
import { ValidationResult } from "./steps/ValidationResult";
import { IdVerificationExtractedDataForm } from "./steps/IdVerificationExtractedDataForm/IdVerificationExtractedDataForm";
import AdditionalData from "../kyc/shared/AdditionalData/AdditionalData";
import AdditionalDataDocIdPictureNieEu from "../kyc/shared/NieEuAdditionalData/steps/AdditionalDataDocIdPictureNieEu";
import { ProcessContext } from "../../../contexts/ProcessContext";
import { DocumentType } from "@nemuru-eng/js-api-client";

export const DocumentIdVerification = () => {
  const {
    state: { idDocumentVerification },
  } = useContext(ProcessContext);

  const isVerificationAlreadyCompleted =
    !!idDocumentVerification.statusFetched &&
    idDocumentVerification.status ===
      ID_DOCUMENT_VERIFICATION_STATUSES.COMPLETED;

  return (
    <DocumentIdVerificationInner
      key={`${
        idDocumentVerification.resetId ?? 0
      }-${isVerificationAlreadyCompleted}`}
      idDocumentVerification={idDocumentVerification}
      isVerificationAlreadyCompleted={isVerificationAlreadyCompleted}
    />
  );
};

const DocumentIdVerificationInner = ({
  idDocumentVerification,
  isVerificationAlreadyCompleted,
}) => {
  const restartingToProcessInformation =
    idDocumentVerification.restartingToProcessInformation ?? false;

  const documentTypeRef = useRef(null);
  documentTypeRef.current =
    idDocumentVerification.documentType ??
    idDocumentVerification.extractedData?.document_type;

  const STEP_GROUP_STEPS_ARRAY = useMemo(
    () =>
      isVerificationAlreadyCompleted
        ? [
            {
              id: DOCUMENT_ID_VERIFICATION_STEP_IDS.ID_VERIFICATION_EXTRACTED_DATA_FORM,
              show: () => true,
            },
            {
              id: DOCUMENT_ID_VERIFICATION_STEP_IDS.NIE_EU_PASSPORT,
              show: () => documentTypeRef.current === DocumentType.NIE_EU,
            },
            {
              id: DOCUMENT_ID_VERIFICATION_STEP_IDS.ADDITIONAL_DATA_GROUP,
              show: () => true,
              isGroup: true,
            },
          ]
        : [
            {
              id: DOCUMENT_ID_VERIFICATION_STEP_IDS.DOCUMENT_TYPE_SELECTION,
              show: () => !restartingToProcessInformation,
            },
            {
              id: DOCUMENT_ID_VERIFICATION_STEP_IDS.PROCESS_INFORMATION,
              show: () => true,
            },
            {
              id: DOCUMENT_ID_VERIFICATION_STEP_IDS.DOCUMENT_ID_PICTURE_FRONT,
              show: () => true,
            },
            {
              id: DOCUMENT_ID_VERIFICATION_STEP_IDS.DOCUMENT_ID_PICTURE_BACK,
              show: (allStepValues) =>
                allStepValues?.[
                  DOCUMENT_ID_VERIFICATION_STEP_IDS.PROCESS_INFORMATION
                ]?.captureMode !== CAPTURE_MODES.UPLOAD,
            },
            {
              id: DOCUMENT_ID_VERIFICATION_STEP_IDS.VALIDATION_RESULT,
              show: () => true,
            },
            {
              id: DOCUMENT_ID_VERIFICATION_STEP_IDS.ID_VERIFICATION_EXTRACTED_DATA_FORM,
              show: () => true,
            },
            {
              id: DOCUMENT_ID_VERIFICATION_STEP_IDS.NIE_EU_PASSPORT,
              show: () => documentTypeRef.current === DocumentType.NIE_EU,
            },
            {
              id: DOCUMENT_ID_VERIFICATION_STEP_IDS.ADDITIONAL_DATA_GROUP,
              show: () => true,
              isGroup: true,
            },
          ],
    [restartingToProcessInformation]
    // isVerificationAlreadyCompleted is stable within a mount (key changes on flip)
  );

  const {
    stepGroupStepToShow,
    submitStep,
    onBack,
    backButtonVisible,
    getStepData,
    currentModuleStepHandler,
  } = useDocumentIdVerification({
    stepGroupStepsArray: STEP_GROUP_STEPS_ARRAY,
  });

  const processInfoData = getStepData(
    DOCUMENT_ID_VERIFICATION_STEP_IDS.PROCESS_INFORMATION
  );

  const captureMode = processInfoData?.captureMode ?? CAPTURE_MODES.CAMERA;

  const forcedStep = useMemo(() => {
    const failedAttempts = idDocumentVerification.failedAttempts ?? 0;
    const maxFailedAttempts = idDocumentVerification.maxFailedAttempts ?? 3;
    if (
      idDocumentVerification.status ===
        ID_DOCUMENT_VERIFICATION_STATUSES.FAILED &&
      failedAttempts >= maxFailedAttempts
    ) {
      return DOCUMENT_ID_VERIFICATION_STEP_IDS.VALIDATION_RESULT;
    }

    const exception = idDocumentVerification.error?.payload?.data?.exception;

    if (!exception) return null;

    if (
      idDocumentVerificationExceptionMatches(
        exception,
        ID_DOCUMENT_VERIFICATION_EXCEPTIONS.NO_PENDING
      )
    ) {
      return DOCUMENT_ID_VERIFICATION_STEP_IDS.DOCUMENT_TYPE_SELECTION;
    }
    if (
      idDocumentVerificationExceptionMatches(
        exception,
        ID_DOCUMENT_VERIFICATION_EXCEPTIONS.PHOTOS_MISSING
      )
    ) {
      return DOCUMENT_ID_VERIFICATION_STEP_IDS.DOCUMENT_ID_PICTURE_FRONT;
    }
    return null;
  }, [
    idDocumentVerification.error,
    idDocumentVerification.status,
    idDocumentVerification.failedAttempts,
    idDocumentVerification.maxFailedAttempts,
  ]);

  const stepToRender = forcedStep ?? stepGroupStepToShow;
  const disableBackButton =
    !!idDocumentVerification.loading || !!idDocumentVerification.error;
  const showBackButton =
    backButtonVisible &&
    stepToRender !==
      DOCUMENT_ID_VERIFICATION_STEP_IDS.DOCUMENT_ID_PICTURE_BACK &&
    stepToRender !== DOCUMENT_ID_VERIFICATION_STEP_IDS.VALIDATION_RESULT &&
    stepToRender !==
      DOCUMENT_ID_VERIFICATION_STEP_IDS.ID_VERIFICATION_EXTRACTED_DATA_FORM;

  return (
    <>
      <Box className="appear-anim" style={{ position: "absolute" }}>
        <BackButton
          onBack={onBack}
          show={showBackButton}
          disabled={disableBackButton}
        />
      </Box>
      {stepToRender ===
        DOCUMENT_ID_VERIFICATION_STEP_IDS.DOCUMENT_TYPE_SELECTION && (
        <DocumentTypeSelection onSubmitForm={submitStep} />
      )}
      {stepToRender ===
        DOCUMENT_ID_VERIFICATION_STEP_IDS.PROCESS_INFORMATION && (
        <ProcessInformation onSubmitForm={submitStep} />
      )}
      {stepToRender ===
        DOCUMENT_ID_VERIFICATION_STEP_IDS.DOCUMENT_ID_PICTURE_FRONT && (
        <DocumentIdPicture
          documentSide={DOCUMENT_SIDES.FRONT}
          captureMode={captureMode}
          onSubmitForm={submitStep}
        />
      )}
      {stepToRender ===
        DOCUMENT_ID_VERIFICATION_STEP_IDS.DOCUMENT_ID_PICTURE_BACK && (
        <DocumentIdPicture
          documentSide={DOCUMENT_SIDES.BACK}
          captureMode={captureMode}
          onSubmitForm={submitStep}
        />
      )}
      {stepToRender === DOCUMENT_ID_VERIFICATION_STEP_IDS.VALIDATION_RESULT && (
        <ValidationResult onSubmitForm={submitStep} />
      )}
      {stepToRender ===
        DOCUMENT_ID_VERIFICATION_STEP_IDS.ID_VERIFICATION_EXTRACTED_DATA_FORM && (
        <IdVerificationExtractedDataForm
          onSubmitForm={submitStep}
          savedValues={getStepData(
            DOCUMENT_ID_VERIFICATION_STEP_IDS.ID_VERIFICATION_EXTRACTED_DATA_FORM
          )}
        />
      )}
      {stepToRender === DOCUMENT_ID_VERIFICATION_STEP_IDS.NIE_EU_PASSPORT && (
        <AdditionalDataDocIdPictureNieEu
          onSubmitForm={submitStep}
          data={getStepData(DOCUMENT_ID_VERIFICATION_STEP_IDS.NIE_EU_PASSPORT)}
        />
      )}
      {stepToRender ===
        DOCUMENT_ID_VERIFICATION_STEP_IDS.ADDITIONAL_DATA_GROUP && (
        <AdditionalData
          showNieEuFunc={() => false}
          currentModuleStepHandler={currentModuleStepHandler}
        />
      )}
    </>
  );
};
